import cn from 'classnames/bind';
import React, { FunctionComponent, useMemo, useCallback } from 'react';
import Modal from 'react-modal';
import { useIntl } from 'react-intl';

import { useFullscreenRef } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Layout/hooks/use-fullscreen-ref';
import { PlayerBaseInfoProvider } from '@/scenarios/legacy-player/components/player-manager/context/PlayerBaseInfoProvider';

import { usePlayerConfig } from '../feature-player/hooks/config/use-player-config';
import { Player } from '../feature-player/ui/player/container';
import { PlayerManagerContainer } from '../player-manager/ui/player-manager/container';
import { usePlayerManagerStreamHandler } from '../player-manager/hooks/use-player-manager-stream-handler';
import { PlayerManagerController } from '../player-manager/ui/player-manager-controller/component';
import { useAutoFocusAfterRemoveFocusedElement } from '../../hooks/useAutoFocusAfterRemoveFocusedElement';

import styles from './PlayerManager.module.css';
import { PlayerManagerProps, PlayerIframeActionType } from './PlayerManager.types';
import { useStreamsErrors } from './parts/hooks';
import { PlayerManagerContext } from './parts/context';

const cx = cn.bind(styles);

const isLaunchedInIframe = () => window.parent && window.parent !== window;

export const PlayerManagerComponent: FunctionComponent<PlayerManagerProps> = (props) => {
    const { playingContentId, stopContent } = props;
    const isModalOpen = Boolean(playingContentId);
    const autoFocusAfterRemoveRef = useAutoFocusAfterRemoveFocusedElement(isModalOpen);
    const { toggleFullscreen, fullscreenRef } = useFullscreenRef(autoFocusAfterRemoveRef);

    const handleClose = useCallback(() => {
        if (isLaunchedInIframe()) {
            window.parent.postMessage({ action: PlayerIframeActionType.PLAYER_CLOSED }, '*');
        }
        if (playingContentId) {
            stopContent(playingContentId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playingContentId]);

    const contextValue = useMemo(
        () => ({
            toggleFullscreen,
        }),
        [toggleFullscreen]
    );

    const isErrorShown = useStreamsErrors(handleClose);

    const intl = useIntl();

    const { config, playerBaseInfoContent, isLoading, contentType, ...configProps } =
        usePlayerConfig({
            streamHandler: usePlayerManagerStreamHandler(),
        });

    return (
        <PlayerManagerContext.Provider value={contextValue}>
            <div className={cx('root')}>
                <Player config={config}>
                    <PlayerManagerController isModalOpen={isModalOpen} />
                    <Modal
                        isOpen={isModalOpen}
                        className={cx('root')}
                        bodyOpenClassName={cx('body')}
                        overlayClassName={cx('overlay')}
                        // turn off stopPropagation on escape keydown - we close player modal on escape keydown manually
                        // https://github.com/reactjs/react-modal/blob/v3.8.2/src/components/ModalPortal.js#L248-L257
                        shouldCloseOnEsc={false}
                        contentLabel={intl.formatMessage({
                            id: 'components.PlayerManager.636da1d35e805b00eae0fcd8333f9234',
                            defaultMessage: 'Player',
                            description: 'Aria-label модального окна с плеером',
                        })}
                    >
                        <div
                            className={cx('browser-view-player', 'browser-view-player_active')}
                            role="presentation"
                            ref={fullscreenRef}
                        >
                            {!isErrorShown && playingContentId ? (
                                <div className={cx('player-wrapper')}>
                                    <PlayerBaseInfoProvider value={playerBaseInfoContent}>
                                        <PlayerManagerContainer
                                            contentId={playingContentId}
                                            onClose={handleClose}
                                            isLoading={isLoading}
                                            contentType={contentType}
                                            {...configProps}
                                        />
                                    </PlayerBaseInfoProvider>
                                </div>
                            ) : null}
                        </div>
                    </Modal>
                </Player>
            </div>
        </PlayerManagerContext.Provider>
    );
};
